import constant from 'constant';

/**
 * Helper for storing data in the ypa-data object in local storage.
 *
 * @param {object} data
 *   A data object that will be merged with the current data in 'ypa-data'.
 * @private
 */
function addToLocalStorage(data: Record<string, any>) {
  const storageData = localStorage.getItem(constant.LOCAL_STORAGE_KEY_NAME);
  const result = {};

  if (storageData) {
    const json = JSON.parse(storageData);
    Object.assign(result, json, data);
  } else {
    Object.assign(result, data);
  }

  localStorage.setItem(constant.LOCAL_STORAGE_KEY_NAME, JSON.stringify(result));
}

/**
 * Helper for retrieving data from the ypa-data object in local storage.
 *
 * Example: To retrieve the values for 'foo' and 'bar', use the following:
 *
 *   import utils from 'utils';
 *   const { foo, bar } = utils.getLocalStorageByKey('foo', 'bar');
 */
function getFromLocalStorage(...keys: string[]) {
  const storageData = localStorage.getItem(constant.LOCAL_STORAGE_KEY_NAME);
  const result: Record<string, any> = {};

  if (storageData) {
    const json = JSON.parse(storageData);

    keys.forEach((key) => {
      if (key in json) {
        result[key] = json[key];
      }
    });
  }

  return result;
}

function clearLocalStorage() {
  localStorage.removeItem(constant.LOCAL_STORAGE_KEY_NAME);
}

function getFormattedDate() {
  const { authenticatedData } = getFromLocalStorage('authenticatedData');
  const dateTimeFormat = authenticatedData?.dateTimeFormat || '';
  const [DayFirst, MonthFirst] = ['D MMM, YYYY, ddd', 'MMM D, YYYY, ddd'];

  switch (dateTimeFormat) {
    case 'mFirst24h':
    case 'mFirstAmPm': {
      return MonthFirst;
    }
    case 'dFirst24h':
    case 'dFirstAmPm': {
      return DayFirst;
    }
    default: {
      return MonthFirst;
    }
  }
}

function isRememberMeChecked() {
  return getFromLocalStorage('rememberMe').rememberMe;
}

function changeDateFormat(newDateFormat: string) {
  const { authenticatedData } = getFromLocalStorage('authenticatedData');
  const dateFormat = authenticatedData?.dateTimeFormat;
  if (dateFormat && newDateFormat) {
    addToLocalStorage({
      authenticatedData: {
        ...authenticatedData,
        dateTimeFormat: newDateFormat,
      },
    });
  }
}

export default {
  setLocalStorage: addToLocalStorage,
  getLocalStorageByKey: getFromLocalStorage,
  clearLocalStorage,
  getFormattedDate,
  isRememberMeChecked,
  changeDateFormat,
};
